import React from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import type {
  NotificationItem,
  NotificationType,
} from '../../../api/types';

/**
 * NotificationCard — Sprint 8 / FE-128.
 *
 * Single row in the NotificationsScreen list (and the bell-popover
 * top-5 surface). Renders:
 *   - Type-aware icon + tint chip on the left
 *   - Title + body + relative timestamp + actor name
 *   - Unread-dot if `read_at` is null
 *   - Tap-anywhere navigates to the target. CTAs (Approve / Decline
 *     / Claim / Acknowledge) are NOT rendered here in v1 — the user
 *     taps the card → deep-links into the relevant screen
 *     (ItemDetail / SharingRequests / GroupDetail / ShareClaim)
 *     where the existing CTAs already live. This keeps the card
 *     visually calm and avoids duplicating action surfaces. Server
 *     auto-marks the row read when the user actions the underlying
 *     domain event (BE-151 fan-out side-effect), so the badge
 *     clears regardless of which surface the user took action from.
 *
 * Dismiss (✕) lives on the right; the entire card is tappable
 * (TouchableOpacity wraps everything; the ✕ stops propagation).
 */

/** Sprint 8 / FE-130b — generic inline-CTA shape rendered below the
 *  notification body. Each notification type can carry 0..2 buttons
 *  (primary on the left in destructive/affirmative color; secondary
 *  on the right as an outlined "soft no" option). */
export interface NotificationCardAction {
  /** Visible button label. Swap to a "Pending…" form via `loading`. */
  label: string;
  /** Tap handler. Caller is responsible for stopPropagation — the
   *  card already wraps the tap in a `stopPropagation` guard. */
  onPress: () => void;
  /** Visual tier:
   *   - 'primary'    = filled brand color (Accept / Claim / Approve)
   *   - 'destructive' = filled error color (recall ack, decline-with-side-effect)
   *   - 'secondary'  = outlined (Decline / Deny — soft no with no harm) */
  variant?: 'primary' | 'secondary' | 'destructive';
  /** Optional MaterialIcons glyph rendered to the left of the label. */
  icon?: keyof typeof MaterialIcons.glyphMap;
  /** True while the action is in-flight — disables both buttons +
   *  swaps the label with an ActivityIndicator. Parent screens drive
   *  this off `useNotificationActions().pendingActionFor(n)`. */
  loading?: boolean;
  /** Test selector — defaults to `notification-action-${index}` if
   *  omitted. Set explicitly for stable test IDs (e.g. 'claim'). */
  testID?: string;
}

/** Sprint 8 follow-up — derived live state of the notification's
 *  underlying entity. Drives the card's visual treatment (disc tint
 *  override + status chip + faded background) so a SHARE_RECEIVED
 *  card that's already been claimed looks resolved at a glance.
 *
 *  Mirrors `NotificationDerivedState` from useNotificationActions —
 *  duplicated as a structural type to avoid a circular import. */
export type NotificationCardState =
  | { kind: 'pending' }
  | { kind: 'claimed'; since: string | null }
  | { kind: 'returned'; since: string | null }
  | { kind: 'declined'; since: string | null }
  | { kind: 'cancelled'; since: string | null }
  | { kind: 'completed'; since: string | null }
  // FE-220 v2 — invite outcomes.
  | { kind: 'joined'; since: string | null }
  | { kind: 'dismissed'; since: string | null }
  | null;

interface NotificationCardProps {
  notification: NotificationItem;
  onPress: () => void;
  onDismiss?: () => void;
  /** Pending-action indicator for mark-read / dismiss (drives the
   *  ✕ spinner). Distinct from inline-CTA loading state. */
  pendingAction?: 'mark-read' | 'dismiss';
  /** Compact mode for the bell popover top-5 (smaller padding,
   *  no dismiss action — that lives only in the full screen). */
  compact?: boolean;
  /** FE-130b — inline action buttons. Pass 0..2 button configs;
   *  rendered as a row below the body in declared order (primary
   *  first on the left). Pass an empty array OR omit entirely to
   *  hide the CTA region. */
  actions?: NotificationCardAction[];

  /** Sprint 8 follow-up — live entity state. When non-null and not
   *  `pending`, the card swaps the disc tint to the state color,
   *  fades the background, and renders a status chip in the body.
   *  Default `null` keeps the existing type-driven styling. */
  state?: NotificationCardState;

  /** Sprint 8 / FE-132 LEGACY — back-compat shortcut for the
   *  CUSTODY_RECALL "Acknowledge & return" CTA. New callers should
   *  use `actions` instead; this is kept so existing call sites
   *  don't break while we migrate. When both `actions` and
   *  `onAcknowledgeRecall` are present, `actions` wins. */
  onAcknowledgeRecall?: () => void;
  isAcknowledgingRecall?: boolean;
}

/** Maps derived state → status chip copy + tint. Returns null when the
 *  state is null/pending (no chip rendered). */
function stateChip(state: NotificationCardState): {
  label: string;
  bg: string;
  fg: string;
  icon: keyof typeof MaterialIcons.glyphMap;
} | null {
  if (!state || state.kind === 'pending') return null;
  switch (state.kind) {
    case 'claimed':
      return {
        label: 'Claimed',
        bg: fpColors.tertiaryContainer,
        fg: fpColors.tertiary,
        icon: 'check-circle',
      };
    case 'returned':
      return {
        label: 'Returned',
        bg: fpColors.tertiaryContainer,
        fg: fpColors.tertiary,
        icon: 'undo',
      };
    case 'completed':
      return {
        label: 'Completed',
        bg: fpColors.tertiaryContainer,
        fg: fpColors.tertiary,
        icon: 'check-circle',
      };
    case 'declined':
      return {
        label: 'Declined',
        bg: fpColors.errorContainer,
        fg: fpColors.error,
        icon: 'block',
      };
    case 'cancelled':
      return {
        label: 'Cancelled',
        bg: fpColors.surfaceVariant,
        fg: fpColors.onSurfaceVariant,
        icon: 'cancel',
      };
    // FE-220 v2 — invite outcomes. Green-toned chip for Joined,
    // muted chip for Dismissed (which covers decline + inviter-cancel).
    case 'joined':
      return {
        label: 'Joined',
        bg: fpColors.primaryContainer,
        fg: fpColors.onPrimaryContainer,
        icon: 'check-circle',
      };
    case 'dismissed':
      return {
        label: 'Dismissed',
        bg: fpColors.surfaceVariant,
        fg: fpColors.onSurfaceVariant,
        icon: 'do-not-disturb-on',
      };
    default:
      return null;
  }
}

/** Override the type's disc color when the entity has been resolved
 *  to a terminal state. Returns null to keep type-driven styling. */
function stateDiscOverride(state: NotificationCardState): {
  tint: string;
  bg: string;
} | null {
  if (!state || state.kind === 'pending') return null;
  if (state.kind === 'claimed' || state.kind === 'returned' || state.kind === 'completed') {
    return { tint: fpColors.tertiary, bg: fpColors.tertiaryContainer };
  }
  if (state.kind === 'declined') {
    return { tint: fpColors.error, bg: fpColors.errorContainer };
  }
  // cancelled
  return { tint: fpColors.onSurfaceVariant, bg: fpColors.surfaceVariant };
}

/** Maps notification type → an Material icon glyph + accent color.
 *  Falls back to a generic bell for unknown types (forward-compat
 *  with new server-side types). */
function typeStyle(type: NotificationType): {
  icon: keyof typeof MaterialIcons.glyphMap;
  tint: string;
  bg: string;
} {
  switch (type) {
    case 'SHARE_RECEIVED':
      return {
        icon: 'card-giftcard',
        tint: fpColors.primary,
        bg: fpColors.primaryContainer,
      };
    case 'SHARE_CLAIMED':
      return {
        icon: 'check-circle',
        tint: fpColors.tertiary,
        bg: fpColors.tertiaryContainer,
      };
    // FE-226 — both legacy CREW_INVITE_* and new GROUP_INVITE_* names
    // resolve to the same visual treatment. GROUP_INVITE_DECLINED is
    // styled differently — colder accent — since it's an outcome not
    // an action ask.
    case 'CREW_INVITE_RECEIVED':
    case 'CREW_INVITE_ACCEPTED':
    case 'GROUP_INVITE_RECEIVED':
    case 'GROUP_INVITE_ACCEPTED':
      return {
        icon: 'group-add',
        tint: fpColors.primary,
        bg: fpColors.primaryContainer,
      };
    case 'CREW_INVITE_DECLINED':
    case 'GROUP_INVITE_DECLINED':
      return {
        icon: 'do-not-disturb-on',
        tint: fpColors.onSurfaceVariant,
        bg: fpColors.surfaceContainer,
      };
    case 'BORROW_REQUEST_RECEIVED':
      return {
        icon: 'pan-tool',
        tint: fpColors.secondary,
        bg: fpColors.secondaryContainer,
      };
    case 'BORROW_APPROVED':
      return {
        icon: 'thumb-up',
        tint: fpColors.tertiary,
        bg: fpColors.tertiaryContainer,
      };
    case 'BORROW_DECLINED':
      return {
        icon: 'block',
        tint: fpColors.error,
        bg: fpColors.errorContainer,
      };
    case 'CUSTODY_HANDOFF':
      return {
        icon: 'swap-horiz',
        tint: fpColors.primary,
        bg: fpColors.primaryContainer,
      };
    case 'CUSTODY_RETURNED':
      return {
        icon: 'undo',
        tint: fpColors.tertiary,
        bg: fpColors.tertiaryContainer,
      };
    case 'CUSTODY_RECALL':
      return {
        icon: 'priority-high',
        tint: fpColors.error,
        bg: fpColors.errorContainer,
      };
    case 'CUSTODY_RECALL_ACK':
      return {
        icon: 'check-circle',
        tint: fpColors.tertiary,
        bg: fpColors.tertiaryContainer,
      };
    // FE-226 — SG-16: owner nudges a holder. Soft visual (bell icon,
    // primary accent) since it's not a state change, just a reminder.
    case 'CUSTODY_REMINDER':
      return {
        icon: 'notifications-active',
        tint: fpColors.primary,
        bg: fpColors.primaryContainer,
      };
    // FE-226 — SG-22 R2: a share auto-cancelled because owner approved
    // a competing borrow request. Outcome — muted styling, no action.
    case 'SHARE_CANCELLED_BY_OWNER':
      return {
        icon: 'unsubscribe',
        tint: fpColors.onSurfaceVariant,
        bg: fpColors.surfaceContainer,
      };
    case 'OVERDUE':
      return {
        icon: 'schedule',
        tint: fpColors.error,
        bg: fpColors.errorContainer,
      };
    case 'WAITLIST_UPDATE':
      return {
        icon: 'queue',
        tint: fpColors.secondary,
        bg: fpColors.secondaryContainer,
      };
    default:
      return {
        icon: 'notifications',
        tint: fpColors.onSurfaceVariant,
        bg: fpColors.surfaceVariant,
      };
  }
}

/** Compact relative timestamp — "12m", "3h", "2d", "Mon", "Apr 28".
 *  Pure function so it's trivially unit-testable. Exported for
 *  the spec. */
export function relativeTimestamp(iso: string, now: Date = new Date()): string {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const diff = now.getTime() - t;
  if (diff < 0) return 'now';
  const seconds = Math.floor(diff / 1000);
  if (seconds < 45) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  if (days < 365) {
    const month = new Date(iso).toLocaleString('en-US', { month: 'short' });
    const day = new Date(iso).getDate();
    return `${month} ${day}`;
  }
  return new Date(iso).getFullYear().toString();
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onPress,
  onDismiss,
  pendingAction,
  compact = false,
  actions,
  state = null,
  onAcknowledgeRecall,
  isAcknowledgingRecall = false,
}) => {
  const typeStyling = typeStyle(notification.type);
  // Sprint 8 follow-up — resolved state overrides the type-driven
  // disc tint. Pending / null state keeps the original colors so
  // CUSTODY_HANDOFF stays purple etc.
  const discOverride = stateDiscOverride(state);
  const { icon } = typeStyling;
  const tint = discOverride?.tint ?? typeStyling.tint;
  const bg = discOverride?.bg ?? typeStyling.bg;
  const chip = stateChip(state);
  const isResolved = chip != null;
  const isUnread = !notification.read_at;
  const isUrgent = notification.priority === 'URGENT' && !isResolved;
  const padding = compact ? 10 : 14;

  // Resolve which actions to render. Precedence:
  //   1. explicit `actions` prop (FE-130b new path) — used as-is
  //   2. legacy `onAcknowledgeRecall` shortcut — synthesize one
  //      destructive-style action so old call sites keep working
  //   3. nothing — no CTA region
  const resolvedActions: NotificationCardAction[] = actions
    ? actions
    : onAcknowledgeRecall && notification.type === 'CUSTODY_RECALL'
      ? [
          {
            label: isAcknowledgingRecall ? 'Returning…' : 'Acknowledge & return',
            onPress: onAcknowledgeRecall,
            variant: 'destructive',
            icon: 'undo',
            loading: isAcknowledgingRecall,
            testID: `notification-recall-ack-${notification.id}`,
          },
        ]
      : [];

  // Background priority: resolved state wins (lightest tone — "done
  // and dusted"), then unread (slightly tinted), then read (plain
  // surface). Keeps the visual sort of: actionable > already-handled.
  const cardBg = isResolved
    ? fpColors.surfaceContainerLowest
    : isUnread
      ? fpColors.surfaceContainerLow
      : fpColors.surface;

  return (
    <View
      style={{
        backgroundColor: cardBg,
        borderLeftWidth: isUrgent ? 3 : 0,
        borderLeftColor: isUrgent ? fpColors.error : 'transparent',
        borderBottomWidth: 1,
        borderBottomColor: fpColors.outlineVariant,
        opacity: isResolved ? 0.86 : 1,
      }}
    >
    <TouchableOpacity
      testID={`notification-card-${notification.id}`}
      onPress={onPress}
      activeOpacity={0.7}
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        paddingVertical: padding,
        paddingHorizontal: 14,
      }}
      accessibilityRole="button"
      accessibilityLabel={`${notification.title}. ${notification.body ?? ''}. ${
        isUnread ? 'Unread.' : 'Read.'
      }`}
    >
      {/* Type-aware icon disc */}
      <View
        style={{
          width: 36,
          height: 36,
          borderRadius: 18,
          backgroundColor: bg,
          justifyContent: 'center',
          alignItems: 'center',
          marginRight: 12,
        }}
      >
        <MaterialIcons name={icon} size={20} color={tint} />
      </View>

      {/* Body */}
      <View style={{ flex: 1 }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <Text
            style={{
              fontFamily: 'Inter_600SemiBold',
              fontSize: 14,
              color: fpColors.onSurface,
              flex: 1,
              marginRight: 8,
            }}
            numberOfLines={2}
          >
            {notification.title}
          </Text>
          <Text
            style={{
              fontFamily: 'Inter_400Regular',
              fontSize: 12,
              color: fpColors.onSurfaceVariant,
              lineHeight: 18,
            }}
          >
            {relativeTimestamp(notification.created_at)}
          </Text>
        </View>
        {notification.body && (
          <Text
            style={{
              fontFamily: 'Inter_400Regular',
              fontSize: 13,
              color: fpColors.onSurfaceVariant,
              marginTop: 2,
            }}
            numberOfLines={compact ? 2 : 3}
          >
            {notification.body}
          </Text>
        )}
        {/* FE-227 v2 — render the owner's decline reason as a quoted
            block on BORROW_DECLINED cards. BE-214 persists this in
            notification.payload.response_message (with `notes` as an
            alternate key per the legacy alias). Skip silently when
            the owner declined without a reason — the card collapses
            back to title + body. Same pattern (quoted block) is
            reused on BORROW_APPROVED to surface the optional
            approve-with-notes message, so a single render path
            covers both. */}
        {(notification.type === 'BORROW_DECLINED' ||
          notification.type === 'BORROW_APPROVED') &&
          (() => {
            const msg = (
              (notification.payload?.response_message as string | undefined) ??
              (notification.payload?.notes as string | undefined) ??
              ''
            ).trim();
            if (!msg) return null;
            const labelName =
              (notification.actor_name ?? '').trim() ||
              (notification.payload?.actor_name as string | undefined)?.trim() ||
              'Owner';
            const labelVerb =
              notification.type === 'BORROW_DECLINED'
                ? "'s reason"
                : ' replied';
            return (
              <View
                style={{
                  marginTop: 8,
                  paddingHorizontal: 10,
                  paddingVertical: 8,
                  borderRadius: 10,
                  backgroundColor:
                    (fpColors as any).surfaceContainerLow ??
                    fpColors.surfaceVariant ??
                    '#F5F0F7',
                  borderLeftWidth: 3,
                  borderLeftColor:
                    notification.type === 'BORROW_DECLINED'
                      ? fpColors.error
                      : fpColors.tertiary,
                }}
                testID={`notification-response-message-${notification.id}`}
              >
                <Text
                  style={{
                    fontFamily: 'Inter_700Bold',
                    fontSize: 11,
                    color: fpColors.onSurfaceVariant,
                    letterSpacing: 0.3,
                    marginBottom: 3,
                  }}
                >
                  {labelName}
                  {labelVerb}
                </Text>
                <Text
                  style={{
                    fontFamily: 'Inter_400Regular',
                    fontSize: 13,
                    color: fpColors.onSurface,
                    lineHeight: 18,
                    fontStyle: 'italic',
                  }}
                  numberOfLines={compact ? 3 : 6}
                >
                  "{msg}"
                </Text>
              </View>
            );
          })()}
        {/* Sprint 8 follow-up — status chip surfaces the live entity
            state (Claimed / Returned / Declined / etc.) so the user
            sees at a glance which notifications still need attention
            vs. which are already handled. Renders only for resolved
            states; pending stays chip-less and keeps the action
            buttons below. */}
        {chip && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              alignSelf: 'flex-start',
              gap: 4,
              marginTop: 6,
              paddingHorizontal: 8,
              paddingVertical: 3,
              borderRadius: 999,
              backgroundColor: chip.bg,
            }}
            testID={`notification-state-chip-${notification.id}`}
            accessibilityLabel={`Status: ${chip.label}`}
          >
            <MaterialIcons name={chip.icon} size={11} color={chip.fg} />
            <Text
              style={{
                fontFamily: 'Inter_600SemiBold',
                fontSize: 11,
                color: chip.fg,
                letterSpacing: 0.2,
              }}
            >
              {chip.label}
            </Text>
          </View>
        )}
        {isUrgent && (
          <Text
            style={{
              fontFamily: 'Inter_600SemiBold',
              fontSize: 11,
              color: fpColors.error,
              marginTop: 4,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
            }}
          >
            Urgent
          </Text>
        )}
      </View>

      {/* Right rail — unread dot + optional dismiss */}
      <View
        style={{
          alignItems: 'center',
          marginLeft: 8,
          minWidth: 18,
        }}
      >
        {isUnread && (
          <View
            testID={`notification-unread-dot-${notification.id}`}
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: fpColors.primary,
              marginTop: 6,
            }}
          />
        )}
        {!compact && onDismiss && (
          <TouchableOpacity
            onPress={(e) => {
              e?.stopPropagation?.();
              onDismiss();
            }}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={{ marginTop: 12, padding: 2 }}
            testID={`notification-dismiss-${notification.id}`}
            accessibilityRole="button"
            accessibilityLabel="Dismiss notification"
          >
            {pendingAction === 'dismiss' ? (
              <ActivityIndicator size="small" color={fpColors.outline} />
            ) : (
              <MaterialIcons
                name="close"
                size={16}
                color={fpColors.outline}
              />
            )}
          </TouchableOpacity>
        )}
      </View>
    </TouchableOpacity>

    {/* Sprint 8 / FE-130b — generic inline action row below the
        body. Renders zero buttons (no chrome), one button (claim,
        accept, recall-ack), or two (primary + secondary like
        Accept/Decline). All buttons stopPropagation so the card-
        level tap-to-navigate stays unaffected. Buttons disable as a
        set while ANY one is in-flight (prevents double-tap fanout). */}
    {resolvedActions.length > 0 && (
      <View
        style={{
          flexDirection: 'row',
          gap: 8,
          paddingHorizontal: 14,
          paddingBottom: padding,
          paddingTop: 0,
        }}
      >
        {resolvedActions.map((a, idx) => (
          <ActionButton
            key={a.testID ?? `notification-action-${notification.id}-${idx}`}
            action={a}
            anySiblingLoading={resolvedActions.some((x) => x.loading)}
            fallbackTestId={`notification-action-${notification.id}-${idx}`}
          />
        ))}
      </View>
    )}
    </View>
  );
};

const ActionButton: React.FC<{
  action: NotificationCardAction;
  anySiblingLoading: boolean;
  fallbackTestId: string;
}> = ({ action, anySiblingLoading, fallbackTestId }) => {
  const variant = action.variant ?? 'primary';
  // Disable both buttons when ANY action is in-flight — prevents the
  // user from racing Approve+Deny while the first call is pending.
  const disabled = action.loading || anySiblingLoading;

  const bg =
    variant === 'destructive'
      ? fpColors.error
      : variant === 'secondary'
        ? 'transparent'
        : fpColors.primary;
  const fg =
    variant === 'destructive'
      ? fpColors.onError
      : variant === 'secondary'
        ? fpColors.onSurface
        : fpColors.onPrimary;
  const border =
    variant === 'secondary' ? fpColors.outline : 'transparent';

  return (
    <TouchableOpacity
      onPress={(e) => {
        e?.stopPropagation?.();
        if (disabled) return;
        action.onPress();
      }}
      disabled={disabled}
      testID={action.testID ?? fallbackTestId}
      accessibilityRole="button"
      accessibilityLabel={action.label}
      style={{
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 10,
        backgroundColor: bg,
        borderWidth: variant === 'secondary' ? 1 : 0,
        borderColor: border,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {action.loading ? (
        <ActivityIndicator size="small" color={fg} />
      ) : action.icon ? (
        <MaterialIcons name={action.icon} size={16} color={fg} />
      ) : null}
      <Text
        style={{
          color: fg,
          fontFamily: 'Inter_700Bold',
          fontSize: 13,
          letterSpacing: 0.2,
        }}
        numberOfLines={1}
      >
        {action.label}
      </Text>
    </TouchableOpacity>
  );
};
